import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DsBoxModule,
  DsButtonModule,
  DsCheckboxModule,
  DsLabelModule,
  DsMessageModule,
  DsTooltipDirectiveModule,
} from '@bmw-ds/components';

import { RetailCrmRelatedDealer } from '../../../customer/clients/retail-crm/model/responses/legal-entities.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { LoadingIndicatorComponent } from '../../../loading/loading-indicator.component';
import { ConsentCheckboxGroup, ConsentMetadata, ConsentSelection, ConsentType } from '../../model/consent-checkbox';
import { ConsentLegalText } from '../../model/nsc-consent-legal-text';
import { ConsentBoxContentComponent } from '../consent-box-content-column/consent-box-content.component';

@Component({
  selector: 'cdf-consent-box',
  imports: [
    DsBoxModule,
    DsButtonModule,
    ConsentBoxContentComponent,
    DsTooltipDirectiveModule,
    DsMessageModule,
    DsCheckboxModule,
    FormsModule,
    DsLabelModule,
    LoadingIndicatorComponent,
  ],
  template: `
    <ds-box elevation="base">
      <ds-box-content class="container-fluid">
        @if (consentSelectionError()) {
          <ds-message [messageText]="i18n('consent.error.loading')" tone="critical" />
        } @else if (consentSelection() === undefined) {
          <cdf-loading-indicator />
        } @else {
          <div class="row">
            @if (consentCheckboxGroups(); as consentCheckboxGroups) {
              <cdf-consent-box-content
                [customerLanguage]="customerLanguage()"
                [(consentSelection)]="consentSelection"
                [legalText]="legalText()"
                [consentCheckboxGroups]="consentCheckboxGroups"
                [initialConsentCreationMode]="initialConsentCreationMode()"
                [onlyImplicitConsentsSelected]="onlyImplicitConsentsSelected()"
                [consentType]="consentType()"
                [consentSaveDate]="consentSaveDate()"
                [consentMetadata]="consentMetadata()"
                [readonlyTooltip]="readonlyTooltip()"
              >
                <!-- copy consent selection checkboxes -->
                @if (consentType() === ConsentType.DEALER) {
                  @if (dealerMandatoryLegalEntities().length === 0) {
                    <cdf-loading-indicator />
                  } @else if (optionalLegalEntities().length > 0) {
                    <ds-checkbox-group size="lg" class="pt-4x">
                      <input
                        ds-input
                        type="checkbox"
                        id="all-legal-entities"
                        [ngModel]="copyAllSelected()"
                        [indeterminate]="copyAllIndeterminate()"
                        (ngModelChange)="
                          updateLegalEntities.emit({ checked: $event, chosenDealers: optionalLegalEntities() })
                        "
                        [disabled]="!!readonlyTooltip()"
                        [ds-tooltip]="readonlyTooltip()"
                      />
                      <label ds-label for="all-legal-entities">{{ i18n('consent.copyForAllLegalEntities') }}</label>
                    </ds-checkbox-group>

                    <details class="ps-4x">
                      <summary>
                        {{ i18n('consent.copyDetailHeader') }}
                      </summary>
                      <ds-checkbox-group size="lg">
                        @for (legalEntity of optionalLegalEntities(); track legalEntity.distributionPartnerNr) {
                          <input
                            ds-input
                            type="checkbox"
                            id="legal-entities-{{ legalEntity.distributionPartnerNr }}"
                            [ngModel]="copySpecificConsentModel(legalEntity)"
                            (ngModelChange)="
                              updateLegalEntities.emit({ checked: $event, chosenDealers: [legalEntity] })
                            "
                            [disabled]="!!readonlyTooltip()"
                            [ds-tooltip]="readonlyTooltip()"
                          />
                          <label ds-label for="legal-entities-{{ legalEntity.distributionPartnerNr }}">
                            {{ legalEntity.distributionPartnerNr }} {{ legalEntity.name }}
                          </label>
                        }
                      </ds-checkbox-group>
                    </details>
                  }
                }

                <!-- save/copy consent legal text and buttons -->
                <div class="confirmation">
                  <h3>{{ i18n('consent.confirmationHeader') }}</h3>
                  <span>
                    {{ i18n('consent.confirmationInstructions') }}
                    {{ consentType() === ConsentType.NSC ? i18n('consent.confirmationNotificationNote') : '' }}
                  </span>

                  <div class="button-container">
                    <ds-button
                      ds-loading
                      [isLoading]="isSaving()"
                      [disabled]="isSaveButtonDisabled() || !!readonlyTooltip()"
                      [ds-tooltip]="readonlyTooltip()"
                      [dsTooltipConfig]="{ disabled: !readonlyTooltip(), origin: 'bottom', showTriggerDelay: 500 }"
                      (click)="confirm.emit()"
                    >
                      {{ i18n('consent.confirmationButton') }}
                    </ds-button>

                    @if (consentType() === ConsentType.NSC && showCopyNscToDealerConsents()) {
                      <ds-button
                        id="copy-consent-button"
                        (click)="copyConsent.emit()"
                        variant="outline"
                        [disabled]="isCopyButtonDisabled() || !!readonlyTooltip()"
                        [ds-tooltip]="readonlyTooltip()!"
                        [dsTooltipConfig]="{ disabled: !readonlyTooltip(), origin: 'bottom', showTriggerDelay: 500 }"
                      >
                        {{ i18n('consent.copyNscConsentsToDealerConsentsButtonLabel') }}
                      </ds-button>
                    }
                  </div>
                </div>
              </cdf-consent-box-content>
            } @else {
              <cdf-loading-indicator />
            }
          </div>
        }
      </ds-box-content>
    </ds-box>
  `,
  styleUrl: './consent-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentBoxComponent {
  readonly consentSelection = model.required<ConsentSelection | undefined>();
  readonly consentSelectionError = input.required<boolean>();
  readonly relatedDealers = input<RetailCrmRelatedDealer[]>();
  readonly dealerLegalEntitiesSelection = input.required<string[]>();
  readonly dealerLegalEntitiesSelectionChange = output<string[]>();
  readonly dealerMandatoryLegalEntities = input.required<string[]>();
  readonly customerLanguage = input.required<string | undefined>();
  readonly consentCheckboxGroups = input.required<ConsentCheckboxGroup[] | undefined>();
  readonly legalText = input.required<ConsentLegalText>();

  readonly canSave = input(true);
  readonly isSaving = input(false);
  readonly onlyImplicitConsentsSelected = input(false);
  readonly initialConsentCreationMode = input(false);
  readonly missingRegistration = input(false);
  readonly consentType = input<ConsentType>(ConsentType.NSC);
  readonly consentSaveDate = input<Date>();
  readonly consentMetadata = input<ConsentMetadata>();
  readonly readonly = input(false);
  readonly showCopyNscToDealerConsents = input(false);

  readonly confirm = output();
  readonly copyConsent = output();
  readonly updateLegalEntities = output<{ checked: boolean; chosenDealers: RetailCrmRelatedDealer[] }>();

  protected readonly i18n = useI18n();

  protected readonly isCopyButtonDisabled = computed(() => !this.consentCheckboxGroups());

  protected readonly isSaveButtonDisabled = computed(
    () => !this.canSave() || this.onlyImplicitConsentsSelected() || this.isCopyButtonDisabled(),
  );

  protected readonly readonlyTooltip = computed(() => {
    if (this.readonly()) {
      return this.i18n('readonlyTooltip');
    } else if (this.missingRegistration()) {
      return this.i18n('consent.missingRegistration', { consentType: this.consentType() });
    } else {
      return '';
    }
  });

  protected readonly optionalLegalEntities = computed(() => {
    const allLegalEntities = this.relatedDealers();
    if (!allLegalEntities) {
      return [];
    }
    const mandatoryLegalEntities = this.dealerMandatoryLegalEntities();
    return allLegalEntities.filter((it) => !mandatoryLegalEntities.includes(it.distributionPartnerNr)).sort();
  });

  protected readonly ConsentType = ConsentType;

  copyAllSelected() {
    return this.optionalLegalEntities().every((it) =>
      this.dealerLegalEntitiesSelection()?.includes(it.distributionPartnerNr),
    );
  }

  copyAllIndeterminate() {
    return (
      !this.copyAllSelected() &&
      this.optionalLegalEntities().some((it) => this.dealerLegalEntitiesSelection()?.includes(it.distributionPartnerNr))
    );
  }

  copySpecificConsentModel(legalEntity: RetailCrmRelatedDealer) {
    return this.dealerLegalEntitiesSelection()?.includes(legalEntity.distributionPartnerNr);
  }
}
